import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Search, 
  Eye,
  HeadphonesIcon,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SupportPage = ({ userType }) => {
  const { t } = useLanguage();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [responseText, setResponseText] = useState('');

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/support`, {
        params: { ticket_type: userType }
      });
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Error al cargar tickets');
    } finally {
      setLoading(false);
    }
  };

  const updateTicket = async (ticketId, status, response = null) => {
    try {
      const updateData = { status };
      if (response) updateData.response = response;
      
      await axios.put(`${API}/support/${ticketId}`, updateData);
      toast.success(t('savedSuccessfully'));
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status, response: response || selectedTicket.response });
      }
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleSendResponse = () => {
    if (!responseText.trim()) return;
    updateTicket(selectedTicket.id, 'in_progress', responseText);
    setResponseText('');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertCircle, label: t('open') },
      in_progress: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock, label: t('inProgress') },
      resolved: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle, label: t('resolved') },
      closed: { color: 'bg-gray-100 text-gray-700 border-gray-200', icon: CheckCircle, label: t('closed') },
    };
    const config = statusConfig[status] || statusConfig.open;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <config.icon size={12} />
        {config.label}
      </Badge>
    );
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.user_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const title = userType === 'client' ? t('supportClients') : t('supportDistributors');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid={`support-${userType}-page`}>
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {title}
        </h1>
        <p className="text-gray-600 mt-1">{t('ticketsList')}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-white border border-[#467322]/20">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-[#467322]/10 rounded-lg">
              <HeadphonesIcon className="text-[#467322]" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Tickets</p>
              <p className="text-2xl font-bold text-black">{tickets.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-[#467322]/20">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-lg">
              <AlertCircle className="text-amber-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Abiertos</p>
              <p className="text-2xl font-bold text-black">
                {tickets.filter(t => t.status === 'open').length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-[#467322]/20">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Clock className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">En Progreso</p>
              <p className="text-2xl font-bold text-black">
                {tickets.filter(t => t.status === 'in_progress').length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-[#467322]/20">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="text-green-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Resueltos</p>
              <p className="text-2xl font-bold text-black">
                {tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-[#467322]/20">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#467322]" size={20} />
              <Input
                placeholder={`${t('search')}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-[#467322]/30 focus:border-[#467322]"
                data-testid="search-tickets"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 border-[#467322]/30">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-[#467322]/20">
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="open">{t('open')}</SelectItem>
                <SelectItem value="in_progress">{t('inProgress')}</SelectItem>
                <SelectItem value="resolved">{t('resolved')}</SelectItem>
                <SelectItem value="closed">{t('closed')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card className="bg-white border border-[#467322]/20">
        <CardHeader>
          <CardTitle className="text-[#467322] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <HeadphonesIcon size={24} />
            {t('ticketsList')} ({filteredTickets.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredTickets.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <HeadphonesIcon size={48} className="mx-auto mb-4 opacity-50" />
              <p>{t('noTickets')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="table-striped">
                <TableHeader>
                  <TableRow className="bg-[#467322]">
                    <TableHead className="text-white font-semibold">{t('subject')}</TableHead>
                    <TableHead className="text-white font-semibold">Usuario</TableHead>
                    <TableHead className="text-white font-semibold">{t('status')}</TableHead>
                    <TableHead className="text-white font-semibold">{t('createdAt')}</TableHead>
                    <TableHead className="text-white font-semibold text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTickets.map((ticket) => (
                    <TableRow key={ticket.id} className="hover:bg-[#F2F7F0]">
                      <TableCell className="font-medium">{ticket.subject}</TableCell>
                      <TableCell>{ticket.user_name}</TableCell>
                      <TableCell>{getStatusBadge(ticket.status)}</TableCell>
                      <TableCell>
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setShowDetailsModal(true);
                          }}
                          className="text-[#467322] hover:bg-[#F2F7F0]"
                          data-testid={`view-ticket-${ticket.id}`}
                        >
                          <Eye size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ticket Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="bg-white border border-[#467322]/20 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Ticket de Soporte
            </DialogTitle>
          </DialogHeader>
          
          {selectedTicket && (
            <div className="space-y-4">
              {/* Ticket Info */}
              <div className="p-4 bg-[#F2F7F0] rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-[#467322]">{selectedTicket.subject}</span>
                  {getStatusBadge(selectedTicket.status)}
                </div>
                <p className="text-sm text-gray-600">
                  De: {selectedTicket.user_name} • {new Date(selectedTicket.created_at).toLocaleString()}
                </p>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label className="text-black">{t('message')}</Label>
                <div className="p-4 bg-white border border-[#467322]/20 rounded-lg">
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedTicket.message}</p>
                </div>
              </div>

              {/* Response */}
              {selectedTicket.response && (
                <div className="space-y-2">
                  <Label className="text-black">{t('response')}</Label>
                  <div className="p-4 bg-[#467322]/10 border border-[#467322]/20 rounded-lg">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedTicket.response}</p>
                  </div>
                </div>
              )}

              {/* Status Update */}
              <div className="space-y-2">
                <Label className="text-black">{t('status')}</Label>
                <Select 
                  value={selectedTicket.status} 
                  onValueChange={(value) => updateTicket(selectedTicket.id, value)}
                >
                  <SelectTrigger className="border-[#467322]/30">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-[#467322]/20">
                    <SelectItem value="open">{t('open')}</SelectItem>
                    <SelectItem value="in_progress">{t('inProgress')}</SelectItem>
                    <SelectItem value="resolved">{t('resolved')}</SelectItem>
                    <SelectItem value="closed">{t('closed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Add Response */}
              <div className="space-y-2">
                <Label className="text-black">Agregar Respuesta</Label>
                <Textarea
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Escribe una respuesta..."
                  className="border-[#467322]/30 focus:border-[#467322]"
                />
                <Button
                  onClick={handleSendResponse}
                  disabled={!responseText.trim()}
                  className="bg-[#467322] hover:bg-[#3A601C] text-white"
                >
                  <MessageSquare size={16} className="mr-2" />
                  Enviar Respuesta
                </Button>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDetailsModal(false)}
              className="border-[#467322] text-[#467322]"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportPage;
